import React, { useEffect, useMemo, useState } from "react";

import "./WSBookMentorship.scss";
import { useAuth } from "../../../../contexts/Auth";
import { InlineWidget, useCalendlyEventListener } from "react-calendly";
import { useParams } from "react-router-dom";
import { connect } from "react-redux";
import { updateFieldsLoad } from "../../../../appRedux/ducks/updateFields";
import { useSnackbar } from "notistack";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";

const WSBookMentorship = ({ tier, themeMode, setUserFields }) => {
  const { userData, sessionsLeftWealth, mentorshipSessionsUsed } = useAuth();
  const { series } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [mentor, setMentor] = useState("dane");
  // Determine if user has auto trading enabled
  const isAutoEnabled = useMemo(() => {
    return !!userData?.feature_flags?.[`wealth_series_${series}_auto`];
  }, [userData, series]);

  const hasSessions = useMemo(() => {
    // Check either regular or auto sessions depending on which feature flag is active

    if (series === "core") {
      return sessionsLeftWealth[series];
    }
    return isAutoEnabled
      ? sessionsLeftWealth[`${series}_auto`]
      : sessionsLeftWealth[series];
  }, [series, sessionsLeftWealth, isAutoEnabled]);

  useCalendlyEventListener({
    onEventScheduled: () => {
      // Update with the correct session key based on auto/regular status
      let sessionKey = "";
      if (series === "core") {
        sessionKey = `wealth_mentorship_${series}`;
      } else {
        sessionKey = `wealth_mentorship_${series}${isAutoEnabled ? "_auto" : ""}`;
      }

      setUserFields({
        training_sessions_used: {
          ...mentorshipSessionsUsed,
          [sessionKey]: (mentorshipSessionsUsed[sessionKey] || 0) + 1,
        },
      });
      enqueueSnackbar(
        "Your session has been booked, please check your email for confirmation!",
        {
          variant: "success",
        },
      );
    },
  });

  const handleChange = (event) => {
    setMentor(event.target.value);
  };

  // Define available mentors based on series
  const availableMentors = useMemo(() => {
    return [
      { value: "roman", label: "Roman Radenski" },
      { value: "dane", label: "Dane Glisek" },
    ];
  }, [series]);

  useEffect(() => {
    if (series === "core") {
      setMentor("roman");
    } else {
      setMentor("dane");
    }
  }, [series]);

  const link = useMemo(() => {
    // Calendly URL mapping for tier + mentor combinations
    const calendlyUrls = {
      core: {
        roman:
          "https://calendly.com/analysts-4/wealth-series-core-mentorship-roman",
        dane: "https://calendly.com/analysts-4/wealth-series-core-dane",
      },
      atlas: {
        roman:
          "https://calendly.com/analysts-4/wealth-series-mentorship-atlas-roman",
        dane: "https://calendly.com/analysts-4/wealth-series-mentorship-atlas-dane",
      },
      titan: {
        roman:
          "https://calendly.com/analysts-4/wealth-series-mentorship-titan-roman",
        dane: "https://calendly.com/analysts-4/wealth-series-mentorship-titan-dane",
      },
      everest: {
        roman:
          "https://calendly.com/analysts-4/wealth-series-mentorship-everest-roman",
        dane: "https://calendly.com/analysts-4/wealth-series-mentorship-everest-dane",
      },
    };

    // Return the URL for the current tier and mentor combination, or null if not found
    return calendlyUrls[tier]?.[mentor] || null;
  }, [tier, mentor]);

  return (
    <div
      className={`ws-book-mentorship ${themeMode === "light" ? "ws-light-calendar" : "ws-dark-calendar"}`}
    >
      {!!hasSessions && series && (
        <div className="ws-book-mentorship__selector">
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">
              Select Your Mentor
            </InputLabel>
            <Select
              value={mentor}
              label="Select Your Mentor"
              onChange={handleChange}
            >
              {availableMentors.map((mentorOption) => (
                <MenuItem key={mentorOption.value} value={mentorOption.value}>
                  {mentorOption.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )}

      {hasSessions && link ? (
        <InlineWidget
          prefill={{
            email: userData?.email || "",
            name:
              (userData?.firstName || "") + " " + (userData?.lastName || ""),
          }}
          url={link}
          pageSettings={{
            hideGdprBanner: true,
          }}
        />
      ) : hasSessions && !link ? (
        <b>Mentorship booking is not available for this tier yet.</b>
      ) : (
        <b>No More Sessions</b>
      )}
    </div>
  );
};
const stateToProps = (state) => ({
  themeMode: state.themeMode.themeMode,
});

const dispatchToProps = (dispatch) => ({
  setUserFields: (fields) => dispatch(updateFieldsLoad(fields)),
});

export default connect(stateToProps, dispatchToProps)(WSBookMentorship);
